import { MAX_REVISIONS, MAX_REVISIONS_JSON_CHARS } from './constants';
import { RevisionField, RevisionOp } from './enums';

/**
 * Revision history of check-in items and projects (data dictionary §3.1): the `revisions` field
 * of the record, oldest first. A creation is one entry without values; every later save that
 * changes a tracked field appends the changed fields with their values before and after.
 */

export interface RevisionChange {
  /** A `RevisionField`; an unknown name (a newer client's) is kept and not displayed. */
  readonly field: string;
  readonly from: unknown;
  readonly to: unknown;
  /**
   * Where the change takes effect: the local date-time (`YYYY-MM-DDTHH:mm`; a personal date in
   * revisions written before occurrences) of a planned-time change (null = all dates), or of a
   * check-in item being disabled. Absent elsewhere.
   */
  readonly effectiveFrom?: string | null;
}

export interface Revision {
  /** ISO 8601 instant of the save. */
  readonly at: string;
  readonly op: RevisionOp;
  readonly changes: readonly RevisionChange[];
}

/** The five recurrence fields of a check-in item, recorded together as the `rule` value. */
export interface RevisionRule {
  readonly repeat: unknown;
  readonly weekdays: unknown;
  readonly intervalDays: unknown;
  readonly startDate: unknown;
  readonly endDate: unknown;
}

const REVISION_OPS: readonly string[] = Object.values(RevisionOp);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function readChange(value: unknown): RevisionChange | null {
  if (!isRecord(value) || typeof value['field'] !== 'string') return null;
  const effective = value['effectiveFrom'];
  return {
    field: value['field'],
    from: value['from'] ?? null,
    to: value['to'] ?? null,
    ...(effective === undefined ? {} : { effectiveFrom: typeof effective === 'string' ? effective : null }),
  };
}

function readRevision(value: unknown): Revision | null {
  if (!isRecord(value)) return null;
  const { at, op, changes } = value;
  if (typeof at !== 'string' || Number.isNaN(Date.parse(at))) return null;
  if (typeof op !== 'string' || !REVISION_OPS.includes(op)) return null;
  if (!Array.isArray(changes)) return null;
  return {
    at,
    op: op as RevisionOp,
    changes: changes.map(readChange).filter((change): change is RevisionChange => change !== null),
  };
}

/** The readable revisions of a record, oldest first; invalid entries are skipped, never rejected. */
export function readRevisions(data: { readonly revisions?: unknown }): Revision[] {
  const stored = data.revisions;
  if (!Array.isArray(stored)) return [];
  return stored.map(readRevision).filter((revision): revision is Revision => revision !== null);
}

/** Structural equality of JSON values (arrays by element, objects by key). */
export function sameJsonValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => sameJsonValue(item, b[index]));
  }
  if (!isRecord(a) || !isRecord(b)) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  // An absent key, an undefined value and null all mean "not set" in a record.
  return [...keys].every((key) => sameJsonValue(a[key] ?? null, b[key] ?? null));
}

type Fields = Readonly<Record<string, unknown>>;

/** The recurrence of a check-in item as one value; absent fields read as null. */
export function revisionRuleOf(data: Fields): RevisionRule {
  return {
    repeat: data['repeat'] ?? null,
    weekdays: data['weekdays'] ?? null,
    intervalDays: data['intervalDays'] ?? null,
    startDate: data['startDate'] ?? null,
    endDate: data['endDate'] ?? null,
  };
}

function change(field: RevisionField, from: unknown, to: unknown): RevisionChange[] {
  return sameJsonValue(from, to) ? [] : [{ field, from, to }];
}

/**
 * The tracked changes between two versions of a check-in item. `plannedFrom` is the effective
 * date of a planned-time change (null = all dates; omit when the caller gave none).
 */
export function checkinItemChanges(
  before: Fields,
  after: Fields,
  plannedFrom?: string | null,
): RevisionChange[] {
  const plannedBefore = before['plannedMin'] ?? null;
  const plannedAfter = after['plannedMin'] ?? null;
  // Moving where an unchanged planned time takes effect is a plan change too.
  const moved = plannedFrom !== undefined && !sameJsonValue(before['schedule'] ?? null, after['schedule'] ?? null);
  const planned =
    sameJsonValue(plannedBefore, plannedAfter) && !moved
      ? []
      : [
          {
            field: RevisionField.PlannedMin,
            from: plannedBefore,
            to: plannedAfter,
            ...(plannedFrom === undefined ? {} : { effectiveFrom: plannedFrom }),
          },
        ];
  const active = change(RevisionField.Active, before['active'] === true, after['active'] === true).map(
    (entry) =>
      entry.to === false
        ? {
            ...entry,
            effectiveFrom:
              typeof after['disabledAt'] === 'string'
                ? after['disabledAt']
                : typeof after['disabledFrom'] === 'string'
                  ? after['disabledFrom']
                  : null,
          }
        : entry,
  );
  return [
    ...change(RevisionField.Name, before['name'], after['name']),
    ...planned,
    ...change(RevisionField.Rule, revisionRuleOf(before), revisionRuleOf(after)),
    ...change(RevisionField.Remind, before['remind'] === true, after['remind'] === true),
    ...active,
  ];
}

/** The tracked changes between two versions of a project. */
export function projectChanges(before: Fields, after: Fields): RevisionChange[] {
  return [
    ...change(RevisionField.Name, before['name'], after['name']),
    ...change(RevisionField.Category, before['category'], after['category']),
    ...change(RevisionField.Color, before['color'], after['color']),
    ...change(
      RevisionField.QuickDurationMin,
      before['quickDurationMin'] ?? null,
      after['quickDurationMin'] ?? null,
    ),
    ...change(RevisionField.Active, before['active'] === true, after['active'] === true),
  ];
}

/** Drops the oldest entries until the history fits `MAX_REVISIONS_JSON_CHARS`; the newest one always stays. */
function trimRevisions(revisions: readonly unknown[]): unknown[] {
  let kept = [...revisions];
  while (kept.length > 1 && JSON.stringify(kept).length > MAX_REVISIONS_JSON_CHARS) kept = kept.slice(1);
  return kept;
}

/**
 * `next` with its revision history brought up to date: a new record (`before` null) starts with
 * one creation entry; an edit with tracked `changes` appends one entry, keeping the most recent
 * `MAX_REVISIONS`; an edit without tracked changes keeps the stored history unchanged.
 */
export function withRevision<T extends Fields>(
  before: Fields | null,
  next: T,
  changes: readonly RevisionChange[],
  at: string,
): T & { readonly revisions?: unknown } {
  if (before === null) {
    return { ...next, revisions: [{ at, op: RevisionOp.Created, changes: [] }] };
  }
  if (changes.length === 0) return next;
  // Stored entries are kept as they are: one this build cannot read (a newer client's) survives.
  const stored: readonly unknown[] = Array.isArray(before['revisions']) ? before['revisions'] : [];
  const revisions = [...stored, { at, op: RevisionOp.Changed, changes }];
  return { ...next, revisions: trimRevisions(revisions.slice(-MAX_REVISIONS)) };
}
